import React from 'react';

// 錯誤堆疊只取前幾行，避免畫面過長、會員截圖截不完整
const MAX_STACK_LINES = 10;

/**
 * 取前 N 行文字
 * @param {String} text
 * @param {Number} maxLines
 * @returns {String}
 */
const takeLines = (text, maxLines) => {
    if (!text) return '';
    return String(text).split('\n').filter((line) => line.trim() !== '').slice(0, maxLines).join('\n');
};

/**
 * 將任意被丟出的值轉成可讀訊息（可能不是 Error 物件，例如直接 throw 字串）
 * @param {*} error
 * @returns {String}
 */
const getErrorMessage = (error) => {
    if (error && typeof error === 'object' && error.message) {
        return `${error.name || 'Error'}: ${error.message}`;
    }
    return String(error);
};

// 刻意使用 inline style、不依賴站台 SCSS：
// 錯誤本身可能就出在樣式或深色主題，獨立樣式才能確保錯誤畫面一定看得到
const styles = {
    root: {
        margin: '16px',
        padding: '16px',
        backgroundColor: '#ffffff',
        color: '#212121',
        borderRadius: '8px',
        fontSize: '14px',
        lineHeight: 1.5,
        textAlign: 'left',
        wordBreak: 'break-all',
    },
    title: {
        margin: '0 0 8px',
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#c62828',
    },
    hint: {
        margin: '0 0 12px',
    },
    label: {
        marginTop: '8px',
        fontWeight: 'bold',
    },
    pre: {
        margin: '4px 0 0',
        padding: '8px',
        backgroundColor: '#f5f5f5',
        fontSize: '12px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
    },
    button: {
        marginTop: '16px',
        padding: '8px 16px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: '#a45bc8',
        color: '#ffffff',
        fontSize: '14px',
    },
};

/**
 * 錯誤邊界：子樹渲染出錯時顯示錯誤資訊，取代 React 整棵卸載造成的白畫面。
 * 錯誤資訊直接顯示在畫面上，方便無法遠端除錯的會員手機截圖回報。
 * @param {Object} props
 * @param {*} props.resetKey 值改變時清除錯誤狀態（傳入路由 pathname，換頁即自動恢復）
 * @param {React.ReactNode} props.children
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, componentStack: '' };
    }

    static getDerivedStateFromError(error) {
        // 用物件包住，避免 throw null / undefined 時被判定成「沒有錯誤」
        return { error: { value: error } };
    }

    componentDidCatch(error, info) {
        this.setState({ componentStack: (info && info.componentStack) || '' });
        console.error('[ErrorBoundary]', error, info);
    }

    componentDidUpdate(prevProps) {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null, componentStack: '' });
        }
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        const { error, componentStack } = this.state;
        if (!error) return this.props.children;

        const rawError = error.value;
        const stack = rawError && typeof rawError === 'object' ? rawError.stack : '';

        return (
            <div style={styles.root}>
                <h2 style={styles.title}>頁面發生錯誤</h2>
                <p style={styles.hint}>請將此畫面截圖提供給店舖教練，協助我們排除問題。</p>

                <div style={styles.label}>錯誤訊息</div>
                <pre style={styles.pre}>{getErrorMessage(rawError)}</pre>

                <div style={styles.label}>環境資訊</div>
                <pre style={styles.pre}>
                    {`版本：${process.env.Version}\n頁面：${window.location.pathname}\n時間：${new Date().toString()}\n瀏覽器：${navigator.userAgent}`}
                </pre>

                {stack && (
                    <React.Fragment>
                        <div style={styles.label}>模組</div>
                        <pre style={styles.pre}>{takeLines(stack, MAX_STACK_LINES)}</pre>
                    </React.Fragment>
                )}

                {componentStack && (
                    <React.Fragment>
                        <div style={styles.label}>介面</div>
                        <pre style={styles.pre}>{takeLines(componentStack, MAX_STACK_LINES)}</pre>
                    </React.Fragment>
                )}

                <button type="button" style={styles.button} onClick={this.handleReload}>
                    重新整理
                </button>
            </div>
        );
    }
}

export default ErrorBoundary;
